import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { toast } from '@ordo/ui/sonner'
import { BackLink } from 'compositions/BackLink'
import { Page } from 'compositions/Page'
import { useCreateLayout } from 'hooks/api'
import { getApiErrorMessage } from 'hooks/useFetchApi'
import { Save } from 'lucide-react'
import { TemplatePreview } from 'pages/templates/template-preview'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'

import { type LayoutFormData, LayoutFormFields } from './layout-form-fields'
const CONTENT_PLACEHOLDER =
  '<div style="background:#f0f0f0;padding:16px;border:1px dashed #999;text-align:center;color:#666">[Template content will appear here]</div>'

function hasContentTag(body: string): boolean {
  return /\{\{\s*content\s*\}\}/.test(body)
}

/** Mirror of the server-side `slugify` util — kept identical so the
 *  auto-filled slug matches the server-side derivation on submit. */
function autoSlug(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function LayoutNew() {
  const navigate = useNavigate()
  const { perform: createLayout, loading: saving } = useCreateLayout()
  const [formData, setFormData] = useState<LayoutFormData>({ name: '', slug: '', body: '' })
  const [slugEdited, setSlugEdited] = useState(false)

  const previewBody = formData.body.replace(/\{\{\s*content\s*\}\}/g, CONTENT_PLACEHOLDER)

  const handleFieldChange = useCallback(
    (field: keyof LayoutFormData, value: string) => {
      setFormData((prev) => {
        const next = { ...prev, [field]: value }
        if (field === 'name' && !slugEdited) {
          next.slug = autoSlug(value)
        }
        if (field === 'slug') {
          setSlugEdited(true)
        }
        return next
      })
    },
    [slugEdited],
  )

  const contentValid = hasContentTag(formData.body)

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      try {
        const created = await createLayout({
          name: formData.name,
          slug: formData.slug || undefined,
          body: formData.body,
        })
        navigate(`/layouts/${created.id}`, { replace: true })
      } catch (err) {
        toast.error('Failed to create layout', { description: getApiErrorMessage(err) })
      }
    },
    [formData, createLayout, navigate],
  )

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/layouts">Back to Layouts</BackLink>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <Card className="min-w-0 flex-1">
          <CardHeader>
            <CardTitle>New Layout</CardTitle>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit}>
              <LayoutFormFields data={formData} onChange={handleFieldChange} />

              {formData.body.trim() && !contentValid && (
                <p className="mt-2 text-sm text-destructive">Layout must contain a {'{{ content }}'} variable.</p>
              )}

              <div className="mt-6 flex justify-end">
                <Button
                  type="submit"
                  disabled={saving || !formData.name.trim() || !formData.body.trim() || !contentValid}>
                  <Save className="mr-1 h-4 w-4" />
                  {saving ? 'Creating...' : 'Create Layout'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <TemplatePreview body={previewBody} mode="html" />
        </div>
      </div>
    </Page>
  )
}
